import Button from "common/Buttons/Button";
import styled from "styled-components";
import { useContext } from "react";
import { SecondaryButton } from "features/configuration/common/styles";
import useMedia from "utils/hooks/useMedia";
import {
  IS_TELEGRAM_WEB_APP,
  MOBILE_SEARCH,
  QUICK_CONNECT_DATA_QUERY_PARAM,
} from "common/constants";
import { OnboardingStep } from "analytics/Avo";
import { AvoContext } from "analytics/AvoProvider";
import useQuickConnectForm from "@shared/features/onboarding/hooks/useQuickConnectForm";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import WhitelistIPsMessage from "features/configuration/accounts/CreateAccount/WhitelistIPsMessage";
import { useUser } from "@shared/utils/hooks/misc";
import ConnectExchangeGuide from "features/portfolio/CreateAccount/ConnectExchangeGuide";
import { OpenConfirmModal } from "@shared/types";
import { useTelegramWebRedirect } from "utils/hooks/misc";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";

interface QuickConnectFormProps {
  primaryExchange?: string;
  openConfirmModal: OpenConfirmModal<any>;
}

const QuickConnectForm = ({
  primaryExchange,
  openConfirmModal,
}: QuickConnectFormProps) => {
  const { avo } = useContext(AvoContext);
  const { closeDrawer, startAccountsPolling } =
    useContext(QuickConnectContext) || {};
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { user } = useUser();
  const { redirectToWebsite, isLoading: isLoadingRedirect } =
    useTelegramWebRedirect();

  const {
    quotaModal,
    noFuturesAccountMessageData,
    quickConnectAlreadyUsedMessageData,
    primaryButtonData,
    secondaryButtonData,
  } = useQuickConnectForm({
    primaryExchange,
    trackAvoConnectClicked: () => {
      if (primaryExchange) {
        avo?.onboardingConnectToExchangeClicked({
          onboardingStep: OnboardingStep.CONNECT_EXCHANGE,
          clientExchange: primaryExchange,
          createClientIsQuickConnect: true,
        });
      }
    },
    openAuthURL: (url) => window.open(url, "_self"),
    useQuotaModal,
    openConfirmModal,
    redirectToWebQuickConnect: IS_TELEGRAM_WEB_APP
      ? (args) => {
          try {
            const parsedQuickConnectData = btoa(JSON.stringify(args));
            redirectToWebsite({
              [QUICK_CONNECT_DATA_QUERY_PARAM]: parsedQuickConnectData,
            }).then(() => {
              startAccountsPolling?.();
              closeDrawer?.();
            });
          } catch {}
        }
      : undefined,
  });

  return (
    <Wrapper>
      {noFuturesAccountMessageData?.displayMessage}
      {quickConnectAlreadyUsedMessageData?.displayMessage}
      <Button
        type="primary"
        $size="l"
        onClick={primaryButtonData.onClick}
        loading={primaryButtonData.loading || isLoadingRedirect}
        disabled={primaryButtonData.disabled}
        style={{
          margin: isMobile ? "0 0 16px 0" : "12px 0 16px 0",
          width: "100%",
        }}
      >
        {primaryButtonData.text}
      </Button>
      <SecondaryButton onClick={secondaryButtonData.onClick}>
        {secondaryButtonData.text}
      </SecondaryButton>
      {isMobile && !user?.is_onboarding && (
        <ConnectExchangeGuide
          primaryExchange={primaryExchange}
          isQuickConnect={true}
        />
      )}
      {user?.is_onboarding && (
        <WhitelistIPsMessage primaryExchange={primaryExchange} />
      )}
      {quotaModal}
    </Wrapper>
  );
};

export default QuickConnectForm;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: 24px;
`;
